import { format } from 'date-fns';
import groupBy from 'ramda/src/groupBy';
import sum from 'ramda/src/sum';
import values from 'ramda/src/values';
import { useMemo } from 'react';
import type { MKEERAApiResponseItem } from '../dashboard.types';
import { excelSerialToDate, parseAmount } from '../utils/dataProcessing';

export function usePaymentsChartData(data: MKEERAApiResponseItem[]) {
  return useMemo(() => {
    // Safety check: ensure data is an array
    if (!Array.isArray(data) || data.length === 0) {
      return {
        total: 0,
        percentChange: 0,
        monthLabels: [],
        chartCounts: [],
      };
    }

    // Group by 'YYYY-MM' using Excel date conversion
    const grouped = groupBy((item: MKEERAApiResponseItem) => {
      try {
        let date: Date;
        const dateString = item.applicationData.dateOfPayment;

        // Check if it's an Excel serial number (numeric string)
        if (/^\d+$/.test(dateString)) {
          date = excelSerialToDate(dateString);
        } else {
          // Handle regular date strings like "7/16/2025"
          date = new Date(dateString);
        }

        return format(date, 'yyyy-MM');
      } catch (error) {
        console.warn(`Failed to process date for item ${item.id}:`, error);
        return 'unknown';
      }
    }, data);

    const sortedMonths = Object.keys(grouped)
      .filter((month) => month !== 'unknown')
      .sort();

    // Aggregate totals and counts per month
    const monthlyTotals: Record<string, number> = {};
    const monthlyCounts: Record<string, number> = {};

    for (const ym of sortedMonths) {
      const apps = grouped[ym] || [];
      const amounts = apps.map((item: MKEERAApiResponseItem) => parseAmount(item.applicationData.amountOfPayment));
      monthlyTotals[ym] = sum(amounts);
      monthlyCounts[ym] = apps.length;
    }

    const total = sum(values(monthlyTotals));

    // Generate short labels (e.g., 'Mar', 'Apr') from months
    const monthLabels = sortedMonths.map((ym) => {
      try {
        const date = new Date(`${ym}-01`);
        return format(date, 'LLL');
      } catch (_error) {
        return ym;
      }
    });

    const chartCounts = sortedMonths.map((ym) => monthlyTotals[ym] || 0);

    // Calculate percent change from last two months
    let percentChange = 0;
    if (sortedMonths.length >= 2) {
      const prev = monthlyTotals[sortedMonths[sortedMonths.length - 2]] || 0;
      const curr = monthlyTotals[sortedMonths[sortedMonths.length - 1]] || 0;
      percentChange = prev === 0 ? 0 : ((curr - prev) / prev) * 100;
    }

    return {
      total,
      percentChange,
      monthLabels,
      chartCounts,
    };
  }, [data]);
}
